import { ChangeEvent, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useDebounce } from '@uidotdev/usehooks';
import { TableAmountPage } from '~/ui/TableAmountPage/TableAmountPage';
import { Board } from '~/components/Shared/Layout/Board/Board';
import { ErrorMessage } from '~/components/Shared/ErrorMessage/ErrorMessage';
import { Button, InputBar, Pagination } from '~/ui';
import '~/assets/css/select-table.css';
import styles from './NotificationsBoard.module.scss';
import { useTranslation } from 'react-i18next';
import { useToastError } from '~/utils/useToastError';
import { NotificationsTable } from './NotificationsTable/NotificationsTable';
import { getPaginate } from '~/utils/getPaginate';
import { PageTransition } from '~/components/Shared/PageTransition/PageTransition';

interface SelectedPage {
  selected: number;
}

export const NotificationsBoard = () => {
  const [sort, setSort] = useState({
    field: '',
    direction: '',
  });
  const { paginate } = getPaginate();
  const [page, setPage] = useState<number>(paginate);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const toastError = useToastError();
  const client = useQueryClient();
  const { t } = useTranslation();

  const params = {
    sort: sort.direction,
    perPage: 5,
    field: sort.field,
    query: debouncedSearchTerm,
    page,
  };

  const {
    data: notifications,
    isError,
    isLoading: isDataLoading,
    error,
  } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/notification', {
        params,
      });
      return data;
    },
    queryKey: ['notifications', params],
    keepPreviousData: true,
  });

  const handleSelect = (id: number) => {
    if (selectedItems.includes(id)) {
      const filteredUsers = selectedItems.filter(
        selectedId => selectedId !== id
      );
      setSelectedItems(filteredUsers);
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleToggleAll = () => {
    if (selectedItems.length === notifications?.data.length) {
      setSelectedItems([]);
    } else {
      const userIdArray = notifications?.data.map((user: any) => user.id);
      setSelectedItems(userIdArray);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await instance.delete('notification-many', {
        data: { notification_ids: selectedItems },
      });
      setSelectedItems([]);
      client.invalidateQueries(['notifications']);
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <Board>
      <div className={styles.notificationsTableOptions}>
        <div className={styles.tableHeadRightWrapper}>
          {!!selectedItems.length && (
            <Button
              text={t('delete')}
              color="red"
              onClick={handleDeleteSelected}
              className={styles.deleteButton}
            />
          )}
          <InputBar
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            value={searchTerm}
            placeholder={t('category_search')}
            classNameInput={styles.notificationsTableInput}
            label={`${t('search')}:`}
            star={false}
            classNameInputWrapper={styles.notificationsTableInputWrapper}
          />
        </div>
      </div>
      <PageTransition page={page}>
        {isError ? (
          <ErrorMessage error={error} />
        ) : (
          <NotificationsTable
            notifications={notifications?.data || []}
            sort={sort}
            setSort={setSort}
            selectedItems={selectedItems}
            handleToggleAll={handleToggleAll}
            handleToggle={handleSelect}
            isLoading={isDataLoading}
          />
        )}
      </PageTransition>
      <div className={styles.notificationsTablePagination}>
        <TableAmountPage
          firstRow={notifications?.from}
          lastRow={notifications?.to}
          total={notifications?.total}
        />
        <Pagination
          forcePage={page}
          pageCount={notifications?.last_page}
          onPageChange={(selectedPage: SelectedPage) => {
            setPage(selectedPage.selected + 1);
          }}
        />
      </div>
    </Board>
  );
};
